import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, Plus, RefreshCcw } from "lucide-react";

const METHODS = ["cash", "card", "upi", "bank_transfer", "corp_credit", "gateway", "voucher", "comp"];
const PURPOSES = ["payment", "security_deposit", "advance_deposit", "refund", "internal_transfer", "comp"];
const STATUS_BADGE = {
  completed: "success", pending: "warning", failed: "error",
  reversed: "error", refunded: "warning",
  partially_allocated: "info", fully_allocated: "success",
};

function fmtMinor(m, cur = "INR") {
  if (m == null) return "—";
  return `${cur} ${(Number(m) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(d) { return (d || "").slice(0, 16).replace("T", " "); }

export default function Payments() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [method, setMethod] = useState("");
  const [outstanding, setOutstanding] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    property_id: "", folio_id: "", amount_minor: 0, method: "cash",
    purpose: "payment", instrument: {},
    card_last4: "", upi_txn_id: "", bank_ref: "", company_id: "",
  });

  const load = async () => {
    try {
      const q = new URLSearchParams();
      if (status) q.append("status", status);
      if (method) q.append("method", method);
      q.append("page_size", "50");
      const [r1, r2] = await Promise.all([
        api.get(`/payments?${q.toString()}`),
        api.get(`/payments/outstanding?page_size=20`),
      ]);
      setItems(r1.data.items || []);
      setTotal(r1.data.total || 0);
      setOutstanding(r2.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, method]);

  const record = async () => {
    if (!form.property_id || !form.amount_minor) { toast.error("Property and amount required"); return; }
    const instrument = {};
    if (form.method === "card" && form.card_last4) instrument.card_last4 = form.card_last4;
    if (form.method === "upi" && form.upi_txn_id) instrument.upi_txn_id = form.upi_txn_id;
    if (form.method === "bank_transfer" && form.bank_ref) instrument.bank_ref = form.bank_ref;
    if (form.method === "corp_credit" && form.company_id) instrument.company_id = form.company_id;
    try {
      const r = await api.post("/payments", {
        property_id: form.property_id,
        folio_id: form.folio_id || undefined,
        company_id: form.company_id || undefined,
        amount_minor: Number(form.amount_minor),
        method: form.method,
        purpose: form.purpose,
        instrument,
        idempotency_key: `UI-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      toast.success(`Payment ${r.data.payment_no} recorded`);
      setOpen(false);
      setForm({ ...form, folio_id: "", amount_minor: 0, card_last4: "", upi_txn_id: "", bank_ref: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <PageHeader
        icon={Wallet} title="Payments & Settlement"
        subtitle={`${total} payment(s) · ${outstanding.length} folio(s) outstanding`}
        action={
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={load} data-testid="payments-refresh"><RefreshCcw className="w-3 h-3 mr-1" /> Refresh</Button>
            <Button size="sm" onClick={() => setOpen(true)} data-testid="payment-record-btn"><Plus className="w-4 h-4 mr-1" /> Record Payment</Button>
          </div>
        }
      />

      {/* Outstanding folios */}
      <Card className="p-0">
        <div className="px-3 py-2 border-b border-neutral-800 text-[11px] uppercase tracking-wider text-neutral-500">Outstanding & Credit Balances</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
              <tr>
                <th className="text-left px-3 py-2">Folio</th>
                <th className="text-left px-3 py-2">Property</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Outstanding</th>
                <th className="text-right px-3 py-2">Credit</th>
              </tr>
            </thead>
            <tbody>
              {outstanding.map((o) => (
                <tr key={o.folio_id} className="border-t border-neutral-800/60">
                  <td className="px-3 py-2 mono"><a href={`/finance/folios/${o.folio_id}`} className="text-orange-400 hover:underline">{o.folio_no}</a></td>
                  <td className="px-3 py-2 mono text-[11px]">{o.property_id}</td>
                  <td className="px-3 py-2"><Badge tone={o.settlement_status === "credit_balance" ? "info" : "warning"}>{o.settlement_status}</Badge></td>
                  <td className="px-3 py-2 text-right mono">{fmtMinor(o.outstanding_minor, o.currency_code)}</td>
                  <td className="px-3 py-2 text-right mono">{fmtMinor(o.credit_balance_minor, o.currency_code)}</td>
                </tr>
              ))}
              {outstanding.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-neutral-500">No outstanding folios</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Payments list */}
      <Card className="p-0">
        <div className="flex gap-3 items-end p-3 border-b border-neutral-800">
          <div>
            <Label className="text-[10px] uppercase text-neutral-500">Status</Label>
            <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 h-9" data-testid="pay-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {["completed","pending","partially_allocated","fully_allocated","reversed","refunded","failed"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-neutral-500">Method</Label>
            <Select value={method || "all"} onValueChange={(v) => setMethod(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 h-9" data-testid="pay-filter-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {METHODS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
              <tr>
                <th className="text-left px-3 py-2">Payment No</th>
                <th className="text-left px-3 py-2">Method</th>
                <th className="text-left px-3 py-2">Purpose</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Folio</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Received</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-neutral-800/60 hover:bg-neutral-900/30">
                  <td className="px-3 py-2 mono">{p.payment_no}</td>
                  <td className="px-3 py-2"><Badge tone="neutral">{p.method}</Badge></td>
                  <td className="px-3 py-2 mono text-[11px]">{p.purpose}</td>
                  <td className="px-3 py-2 text-right mono">{fmtMinor(p.amount_minor, p.currency_code)}</td>
                  <td className="px-3 py-2 mono text-[10px] text-neutral-500">{(p.folio_id || "").slice(-8) || "—"}</td>
                  <td className="px-3 py-2"><Badge tone={STATUS_BADGE[p.status] || "neutral"}>{p.status}</Badge></td>
                  <td className="px-3 py-2 mono text-[11px] text-neutral-500">{fmtDate(p.received_at)}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-neutral-500">No payments yet</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><Label>Property ID *</Label><Input value={form.property_id} onChange={(e) => setForm({...form, property_id: e.target.value})} data-testid="pay-form-property" /></div>
            <div><Label>Folio ID</Label><Input value={form.folio_id} onChange={(e) => setForm({...form, folio_id: e.target.value})} className="mono" data-testid="pay-form-folio" /></div>
            <div><Label>Amount (minor units)</Label><Input type="number" value={form.amount_minor} onChange={(e) => setForm({...form, amount_minor: e.target.value})} className="mono" data-testid="pay-form-amount" /></div>
            <div>
              <Label>Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({...form, method: v})}>
                <SelectTrigger data-testid="pay-form-method"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Purpose</Label>
              <Select value={form.purpose} onValueChange={(v) => setForm({...form, purpose: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PURPOSES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.method === "card" && <div className="col-span-2"><Label>Card last 4</Label><Input value={form.card_last4} onChange={(e) => setForm({...form, card_last4: e.target.value})} className="mono" maxLength={4} /></div>}
            {form.method === "upi" && <div className="col-span-2"><Label>UPI Txn ID</Label><Input value={form.upi_txn_id} onChange={(e) => setForm({...form, upi_txn_id: e.target.value})} className="mono" /></div>}
            {form.method === "bank_transfer" && <div className="col-span-2"><Label>Bank Ref</Label><Input value={form.bank_ref} onChange={(e) => setForm({...form, bank_ref: e.target.value})} className="mono" /></div>}
            {form.method === "corp_credit" && <div className="col-span-2"><Label>Company ID</Label><Input value={form.company_id} onChange={(e) => setForm({...form, company_id: e.target.value})} className="mono" /></div>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={record} data-testid="pay-form-submit">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
